import { httpClient } from 'redux/reducers/settings';
import { api, TMSResponse } from './service-api';
import { useQuery } from 'react-query';
import { TraineeModule } from '../types/TrainingModule';
import { CourseSession } from '../types/CourseSession';
import { TrainingRoutine } from '../types/GetRoutine';
import { IRoutineByID } from '../types/RoutineById';
import { IDailySessions } from '../types/DailySession';
import { ITrainingDetail } from '../types/TrainingDetail';

export interface PaginatedResponse<T = any> {
  count: number;
  next: number | null;
  previous: number | null;
  results: T;
}

export interface ICourseRoster {
  course: string;
  id: number;
  training_name: string;
  batch_name: string;
  start_date: string;
  end_date: string;
  hall: any;
  coordinators: string[];
  module_coordinator: string[];
  routine_sessions: RoutineSession[];
}

export interface RoutineSession {
  id: number;
  session_name: string;
  start_time: string;
  end_time: string;
  daily_sessions: DailySession[];
}
export interface Dashboardcount {
  attendance_count: number;
  message: string;
  status: string;
  statuscode: number;
  trainee_enroll_count: number;
}

export interface DailySession {
  routine_date: string;
  lecturers: string[];
  altered_start_time?: string;
  course_topic?: string;
  altered_end_time: any;
}

const fetchDashboardcount = () => {
  return httpClient.get<Dashboardcount>(api.dashboardCount);
};

const useDashboardcountQuery = () => {
  return useQuery(api.dashboardCount, fetchDashboardcount, {
    select: (item) => {
      return item?.data;
    }
  });
};
// fetch trainee module

const fetchTraineeModule = () => {
  return httpClient.get<TraineeModule>(api.traineeModule);
};

const useTraineeModuleQuery = () => {
  return useQuery(api.traineeModule, fetchTraineeModule, {
    select: (item) => {
      return item?.data.data;
    }
  });
};
// fetch trainee module

// fetch courseSession
const fetchCourseSession = (courseId: string) => () => {
  return httpClient.get<CourseSession>(api.courseSession.replace('{courseId}', courseId));
};

const useCourseSessionQuery = (courseId: string) => {
  return useQuery([api.courseSession, courseId], fetchCourseSession(courseId), {
    enabled: !!courseId,
    select: (item) => item?.data.data
  });
};

// fetch courseSession

// fetch trainning
const fetchTrainingRoutine = (routine: string) => () => {
  return httpClient.get<TrainingRoutine>(`${api.trainingRoutine}routines/`, {
    params: {
      filter: routine
    }
  });
};

const useTrainingRoutineQuery = (routine: string) => {
  return useQuery([api.trainingRoutine, routine], fetchTrainingRoutine(routine), {
    select: (item) => item?.data?.data?.routines,
    enabled: !!routine
  });
};
// fetch trainning

// fetch routine by id and date
export const fetchRoutineById = (routine_id: number | undefined) => () => {
  return httpClient.get<IRoutineByID>(`${api.routineById}${routine_id}/`);
};

const useRoutineById = (routine_id: number | undefined) => {
  return useQuery([routine_id], fetchRoutineById(routine_id), {
    select: (item) => {
      return item?.data.data;
    },
    enabled: !!routine_id
  });
};

//fetch daily sessions by id and session
export const fetchDailySessionByDate = (date: any, routine_id: number) => () => {
  return httpClient.get<IDailySessions>(`${api.dailySession}${routine_id}/daily-sessions/`, {
    params: {
      date: date
    }
  });
};

const useDailySessionsId = (date: string | null, routine_id: number) => {
  return useQuery([api.dailySession], fetchDailySessionByDate(date, routine_id), {
    select: (item) => item?.data.data
  });
};

//fetch daily sessions by id and session

// training detail fetch
export const fetchTrainingDetail = (training_id: string) => () => {
  return httpClient.get<any>(`${api.trainingDetail}/${training_id}/`);
};

const useTrainingDetailQuery = (training_id: string) => {
  return useQuery([api.trainingDetail], fetchTrainingDetail(training_id), {
    select: (item) => {
      return item.data.data;
    },
    enabled: !!training_id
  });
};

export const fetchRoutineCourseSessionList =
  ({ routineId, courseId }: { routineId: string; courseId: string }) =>
  () => {
    return httpClient.get<any>(`${api.routineCourseSessionList}/${courseId}/?routine=${routineId}`);
  };

const useRoutineCourseSessionListQuery = (routineId: string, courseId: string) => {
  return useQuery(
    [api.routineCourseSessionList, routineId, courseId],
    fetchRoutineCourseSessionList({ routineId, courseId }),
    {
      select: (response) => response.data,
      enabled: !!routineId && !!courseId
    }
  );
};

export const fetchCoordinatorList =
  ({ routineId, courseId }: { routineId: string; courseId: string }) =>
  () => {
    return httpClient.get<any>(`${api.lecturersbyModule}/${courseId}/?routine=${routineId}`);
  };

const useLecturersByModuleQuery = (routineId: string, courseId: string) => {
  return useQuery(
    [api.lecturersbyModule, routineId, courseId],
    fetchCoordinatorList({ routineId, courseId }),
    {
      select: (response) => response.data,
      enabled: !!routineId && !!courseId
    }
  );
};
export const fetchAdminDetail =
  ({ id }: { id: string }) =>
  () => {
    return httpClient.get<any>(`${api.adminDetailById}/${id}/`);
  };

const useAdminDetailByIdQuery = (id: string) => {
  return useQuery([api.adminDetailById, id], fetchAdminDetail({ id }), {
    select: (response) => response.data,
    enabled: !!id
  });
};

// training detail fetch

const fetchTrainingRoster =
  ({ routineId, courseId }: { routineId: string; courseId: string }) =>
  () => {
    return httpClient.get<TMSResponse<ICourseRoster>>(
      api.courseRoster.replace('{routineId}', routineId).replace('{courseId}', courseId)
    );
  };

const useCourseRosterQuery = (routineId: string, courseId: string) => {
  return useQuery(
    [api.courseRoster, routineId, courseId],
    fetchTrainingRoster({ routineId, courseId }),
    {
      select: (response) => {
        return response.data;
      },
      enabled: !!routineId && !!courseId
    }
  );
};
export {
  useDashboardcountQuery,
  useTraineeModuleQuery,
  useCourseSessionQuery,
  useTrainingRoutineQuery,
  useRoutineById,
  useDailySessionsId,
  useTrainingDetailQuery,
  useCourseRosterQuery,
  useRoutineCourseSessionListQuery,
  useLecturersByModuleQuery,
  useAdminDetailByIdQuery
};
